// Post size constants and mappings
export const POST_SIZE_OPTIONS = ['6x6', '8x8', '10x10'] as const;

// Map display names to enum names for backend
export const postSizeDisplayToEnum: Record<string, string> = {
  '6x6': 'SixBySix',
  '8x8': 'EightByEight',
  '10x10': 'TenByTen',
};

// Map enum names to display names from backend
export const postSizeEnumToDisplay: Record<string, string> = {
  SixBySix: '6x6',
  EightByEight: '8x8',
  TenByTen: '10x10',
};

/** Normalizes API enum or display post size to a display value (6x6, 8x8, 10x10). */
export function normalizeBuildingPostSize(postSize: string | null | undefined): string | null {
  if (!postSize?.trim()) {
    return null;
  }

  const trimmed = postSize.trim();
  return postSizeEnumToDisplay[trimmed] ?? trimmed;
}

/** Resolves building post size for truss calculations from form and/or API values. */
export function resolveBuildingPostSizeDisplay(
  buildingFormPostSize?: string | null,
  selectedBuildingPostSize?: string | null
): string {
  return (
    normalizeBuildingPostSize(buildingFormPostSize) ||
    normalizeBuildingPostSize(selectedBuildingPostSize) ||
    '6x6'
  );
}

/** Supported Lean-To post size override values (matches building post sizes and backend PostSize enum). */
export const LEAN_TO_POST_SIZE_OVERRIDE_OPTIONS = POST_SIZE_OPTIONS;

/**
 * Normalizes legacy override values. 4x4 was offered in the UI but never supported by the
 * backend (it silently resolved to 6x6).
 */
export function normalizeLeanToPostSizeOverrideValue(
  value: string | null | undefined
): string | null {
  if (!value?.trim()) return null;

  const displayValue = postSizeEnumToDisplay[value] ?? value;
  if (displayValue === '4x4') return '6x6';

  return (LEAN_TO_POST_SIZE_OVERRIDE_OPTIONS as readonly string[]).includes(displayValue)
    ? displayValue
    : null;
}

// Bay spacing options
export const BAY_SPACING_OPTIONS = [8, 10, 12, 14, 16, 18, 20] as const;

// Width Measurement Reference enum
export enum WidthMeasurementReference {
  InsideInside = 'InsideInside',
  OutsideOutside = 'OutsideOutside',
  SurfaceInside = 'SurfaceInside',
  SurfaceOutside = 'SurfaceOutside',
}

// Width Measurement Reference display labels
export const WIDTH_MEASUREMENT_LABELS: Record<WidthMeasurementReference, string> = {
  [WidthMeasurementReference.InsideInside]: 'Inside Post to Inside Post',
  [WidthMeasurementReference.OutsideOutside]: 'Outside Post to Outside Post',
  [WidthMeasurementReference.SurfaceInside]: 'Surface to Inside Post (Lean-To)',
  [WidthMeasurementReference.SurfaceOutside]: 'Surface to Outside Post (Lean-To)',
};

// Valid measurement references by product type
export const VALID_MEASUREMENT_REFERENCES_BY_PRODUCT: Record<string, WidthMeasurementReference[]> =
  {
    Gable: [WidthMeasurementReference.InsideInside, WidthMeasurementReference.OutsideOutside],
    Header: [WidthMeasurementReference.InsideInside, WidthMeasurementReference.OutsideOutside],
    'Lean-To': [
      WidthMeasurementReference.InsideInside,
      WidthMeasurementReference.OutsideOutside,
      WidthMeasurementReference.SurfaceInside,
      WidthMeasurementReference.SurfaceOutside,
    ],
  };
